/**
 * AuditLogScreen — paginated tenant_access_log viewer.
 *
 * The FERPA "accounting of disclosures" requirement for K-12 ed-tech
 * products. A district IT admin needs to be able to show, on
 * demand, who accessed what student record when. This screen
 * queries the existing tenant_access_log table (already populated
 * by the iOS app + Edge Functions + web client) and renders it
 * in a paginated table with a CSV export button.
 *
 * CSV export uses a client-side Blob — no server round-trip needed.
 * For an audit trail of the export itself, we INSERT a
 * tenant_access_log row with action='read_batched' so the audit
 * log shows "admin exported 30 days of access logs on YYYY-MM-DD".
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { EmptyState, ListIllustration } from '../../components/EmptyState';
import { SkeletonBox } from '../../components/Skeleton';
import { humanError } from '../../lib/ui/errorCopy';
import { DataTable, type DataTableColumn } from '../../components/DataTable';

interface AuditRow {
  accessed_at: string;
  action: string;
  resource_kind: string;
  resource_id: string | null;
  target_organization_id: string | null;
  accessed_by: string;
}

interface Props {
  orgId: string;
}

const PAGE_SIZE = 50;

function toCsv(rows: AuditRow[]): string {
  const header = [
    'accessed_at',
    'action',
    'resource_kind',
    'resource_id',
    'target_organization_id',
    'accessed_by',
  ].join(',');
  const body = rows
    .map((r) =>
      [
        r.accessed_at,
        r.action,
        r.resource_kind,
        r.resource_id ?? '',
        r.target_organization_id ?? '',
        r.accessed_by,
      ]
        .map((field) => `"${String(field).replace(/"/g, '""')}"`)
        .join(','),
    )
    .join('\n');
  return `${header}\n${body}`;
}

export function AuditLogScreen({ orgId }: Props): ReactElement {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  async function load(): Promise<void> {
    setLoading(true);
    const { data, error: e } = await supabase
      .from('tenant_access_log')
      .select('accessed_at, action, resource_kind, resource_id, target_organization_id, accessed_by')
      .eq('target_organization_id', orgId)
      .order('accessed_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (e) {
      setError(humanError(e));
    } else {
      setRows((data ?? []) as AuditRow[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, page]);

  const auditColumns: DataTableColumn<AuditRow>[] = [
    {
      header: 'Accessed at',
      isRowHeader: true,
      cell: (r) => new Date(r.accessed_at).toLocaleString(),
    },
    {
      header: 'Action',
      cell: (r) => r.action,
    },
    {
      header: 'Resource',
      cell: (r) => (
        <>
          {r.resource_kind}
          {r.resource_id ? ` · ${r.resource_id.slice(0, 8)}` : ''}
        </>
      ),
    },
    {
      header: 'By',
      cell: (r) => r.accessed_by.slice(0, 12),
    },
  ];

  function handleExportCsv(): void {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${orgId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    // Record the export in the audit log itself so the FERPA paper
    // trail includes "the admin exported the log on YYYY-MM-DD".
    void supabase.from('tenant_access_log').insert({
      action: 'read_batched',
      resource_kind: 'tenant_access_log',
      resource_id: orgId,
      target_organization_id: orgId,
      accessed_by: 'admin-self',
    });
  }

  return (
    <div className="admin-audit">
      <h2>Audit log</h2>
      <p className="tagline">
        FERPA &ldquo;accounting of disclosures&rdquo; for your district.
        Click Export CSV to download the current page; every export
        is itself recorded here.
      </p>

      <div className="admin-audit-actions">
        <button
          type="button"
          className="snap-button"
          onClick={handleExportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          ← Previous
        </button>
        <span className="tagline">Page {page + 1}</span>
        <button
          type="button"
          className="link-button"
          onClick={() => setPage((p) => p + 1)}
          disabled={rows.length < PAGE_SIZE}
        >
          Next →
        </button>
      </div>

      {error && (
        <section className="empty-state" aria-live="assertive">
          <h2 className="empty-state-title">
            Couldn&rsquo;t load the audit log
          </h2>
          <p className="empty-state-body">{error}</p>
          <button
            type="button"
            className="snap-button empty-state-cta"
            onClick={() => {
              void load();
            }}
          >
            Try again
          </button>
        </section>
      )}
      {loading ? (
        <div
          className="skeleton"
          role="status"
          aria-busy="true"
          aria-label="Loading audit log"
        >
          {Array.from({ length: 4 }, (_, i) => (
            <SkeletonBox key={i} height={28} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          illustration={<ListIllustration />}
          title="No audit entries yet"
          body="Activity will appear here as your team uses EffectiveSLP. Until then, your district has a clean log."
        />
      ) : (
        <DataTable<AuditRow>
          caption="Audit log"
          ariaLabel="Tenant audit log"
          rowKey={(r, i) => `${r.accessed_at}-${r.accessed_by}-${i}`}
          columns={auditColumns}
          rows={rows}
        />
      )}
    </div>
  );
}